import { useNavigate } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';

import { paths } from 'src/routes/paths';
import { useSearchParams } from 'src/routes/hooks';

import * as Supplier from 'src/api/supplier';
import { queryClient } from 'src/api/query-client';

import type { SupplierContractsApplications } from '../SubmitApplication/schema';

export const useInitEditApplication = (applicationId: number) =>
  useMutation({
    mutationFn: () => Supplier.initEditApplication(applicationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplierApplication', applicationId] });
    },
  });

export const STEPS = ['introduction', 'activities', 'qualitative-questions'];

export const useGetSupplierContractApplication = ({
  applicationId,
  enabled,
}: {
  applicationId: number;
  enabled: boolean;
}) =>
  useQuery({
    queryKey: ['supplierApplication', applicationId],
    queryFn: () => Supplier.getSupplierApplication(applicationId),
    enabled,
  });

export const useSaveSupplierContractApplication = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const saveSupplierContractApplicationMutation = useMutation({
    mutationFn: Supplier.updateSupplierContractApplication,
  });

  const updateSupplierContractApplication = async (
    applicationId: number,
    data: SupplierContractsApplications,
    isNext: boolean,
    hasQualitativeQuestions: boolean,
    sessionId: string,
    onError?: (message: string) => void
  ) => {
    let _STEPS = STEPS;
    if (!hasQualitativeQuestions) {
      _STEPS = _STEPS.filter((step) => step !== 'qualitative-questions');
    }
    const currentStep = searchParams.get('step');
    const nextStep = _STEPS[_STEPS.indexOf(currentStep || '') + 1];
    const isLastStep = _STEPS.indexOf(currentStep || '') === _STEPS.length - 1;

    try {
      await saveSupplierContractApplicationMutation.mutateAsync({
        applicationId,
        ...data,
        isSubmit: isNext && isLastStep,
        sessionId,
      });

      queryClient.invalidateQueries({ queryKey: ['application', applicationId] });
      queryClient.invalidateQueries({ queryKey: ['supplierApplication', applicationId] });
      queryClient.invalidateQueries({ queryKey: ['open-contracts'] });

      if (isNext) {
        if (isLastStep) {
          navigate(paths.supplier.success.replace(':id', applicationId.toString()));
        } else if (nextStep) {
          setSearchParams({ step: nextStep });
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    } catch (error: any) {
      onError?.(error?.message || 'Something went wrong, please try again.');
      throw error;
    }
  };

  return {
    updateSupplierContractApplication,
    isPending: saveSupplierContractApplicationMutation.isPending,
  };
};
